import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Lock, Trash2 } from 'lucide-react';

import { getDescendants } from '../../utils/permissionTree';
import ConfirmActionModal from '../member-detail/ConfirmActionModal';
import RoleStudioCard from './RoleStudioCard';

/** Every permission id of a studio tree, parents included. */
const idsOfTree = (tree) => new Set(tree.flatMap((node) => [node.id, ...getDescendants(node).map((d) => d.id)]));

/**
 * Permissions tab of the role detail: one card per studio, as in the prototype. A system role is
 * view only; a custom role edits one studio at a time. Saving merges that studio's grants into the
 * role's other grants and `onSave({ permissions, studios })` resolves `true` when it worked.
 *
 * @param {object} props
 * @param {object} props.role UI role (see `mapApiRoleToUi`)
 * @param {object[]} props.studios Catalog studios with their `tree`.
 * @param {(patch: { permissions: object[], studios: string[] }) => Promise<boolean>} props.onSave
 */
function RolePermissionsTab({ role, studios, onSave }) {
  const { t } = useTranslation('people-access');
  const [openId, setOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [toRemove, setToRemove] = useState(null);

  const idsByStudio = useMemo(() => new Map(studios.map((studio) => [studio.id, idsOfTree(studio.tree)])), [studios]);
  const grantsByStudio = useMemo(
    () =>
      new Map(
        studios.map((studio) => [
          studio.id,
          role.permissions.filter((grant) => idsByStudio.get(studio.id).has(grant.permission_id)),
        ]),
      ),
    [studios, role.permissions, idsByStudio],
  );

  const handleToggleOpen = useCallback((studioId) => setOpenId((current) => (current === studioId ? null : studioId)), []);
  const handleEdit = useCallback((studioId) => {
    setOpenId(studioId);
    setEditingId(studioId);
  }, []);
  const handleStopEditing = useCallback(() => setEditingId(null), []);

  const saveStudio = useCallback(
    (studio, studioGrants) => {
      const studioIds = idsByStudio.get(studio.id);
      const permissions = [...role.permissions.filter((grant) => !studioIds.has(grant.permission_id)), ...studioGrants];
      const covered = studios
        .filter((s) => permissions.some((grant) => idsByStudio.get(s.id).has(grant.permission_id)))
        .map((s) => s.id);
      return onSave({ permissions, studios: covered });
    },
    [idsByStudio, role.permissions, studios, onSave],
  );

  const handleRemoveAccess = useCallback((studio) => setToRemove(studio), []);
  const closeRemove = useCallback(() => setToRemove(null), []);
  const confirmRemove = useCallback(async () => {
    const ok = await saveStudio(toRemove, []);
    if (ok) setEditingId(null);
    return ok;
  }, [saveStudio, toRemove]);

  return (
    <div className="flex flex-col gap-3" data-testid="role-permissions">
      {role.isSystem ? (
        <p className="flex items-center gap-1.5 text-xs text-content-secondary">
          <Lock size={12} aria-hidden />
          {t('roles.detail.systemNote')}
        </p>
      ) : null}
      {studios.map((studio) => (
        <RoleStudioCard
          key={studio.id}
          studio={studio}
          grants={grantsByStudio.get(studio.id)}
          isSystem={role.isSystem}
          isOpen={openId === studio.id}
          isEditing={editingId === studio.id}
          onToggleOpen={handleToggleOpen}
          onEdit={handleEdit}
          onStopEditing={handleStopEditing}
          onSave={saveStudio}
          onRemoveAccess={handleRemoveAccess}
        />
      ))}
      <ConfirmActionModal
        isOpen={Boolean(toRemove)}
        icon={Trash2}
        title={t('roles.detail.permissions.removeModal.title', { studio: toRemove?.name ?? '', role: role.name })}
        message={t('roles.detail.permissions.removeModal.message')}
        confirmLabel={t('roles.detail.permissions.removeModal.confirm')}
        confirmVariant="danger"
        onClose={closeRemove}
        onConfirm={confirmRemove}
      />
    </div>
  );
}

export default memo(RolePermissionsTab);
